import * as SharedLib from '@cumul.io/shared-cumulio-dashboard';
import { html, LitElement, PropertyValues } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { StyleInfo, styleMap } from 'lit/directives/style-map.js';
import * as pkg from '../../../package.json';
import CumulioService from '../cumulio-service';
import { _setEventHandler } from '../helper/event-bus-handler';
import { dashboardStyles } from './dashboard-styles';

@customElement('cumulio-dashboard')
export class CumulioDashboard extends LitElement implements SharedLib.ES6CumulioDashboard {
  // Get container from shadow DOM for containerwidth
  @query('.dashboard-container') container!: HTMLElement;

  // Get iframe for post messages
  @query('iframe') iframe!: HTMLIFrameElement;
  // Input properties
  @property() dashboardId?: SharedLib.DashboardInputs['dashboardId'];
  @property() dashboardSlug?: SharedLib.DashboardInputs['dashboardSlug'];
  @property() itemId?: SharedLib.DashboardInputs['itemId'];
  @property() language: SharedLib.DashboardInputs['language'] = 'auto';
  @property() qeVersion: SharedLib.DashboardInputs['qeVersion'];
  @property() screenMode: SharedLib.DashboardInputs['screenMode'] = 'auto';
  @property({
    converter: {
      fromAttribute: (value) => value === 'true',
      toAttribute: (value) => String(value)
    }
  })
  switchScreenModeOnResize = true;
  @property() editMode: SharedLib.DashboardInputs['editMode'] = 'view';
  @property() mainColor?: SharedLib.DashboardInputs['mainColor'];
  @property() accentColor?: SharedLib.DashboardInputs['accentColor'];
  @property() authToken?: SharedLib.DashboardInputs['authToken'];
  @property() authKey?: SharedLib.DashboardInputs['authKey'];
  @property() loaderBackground?: SharedLib.DashboardInputs['loaderBackground'];
  @property() loaderFontColor?: SharedLib.DashboardInputs['loaderFontColor'];
  @property() loaderSpinnerColor?: SharedLib.DashboardInputs['loaderSpinnerColor'];
  @property() loaderSpinnerBackground?: SharedLib.DashboardInputs['loaderSpinnerBackground'];
  @property() appServer?: SharedLib.DashboardInputs['appServer'];
  @property() timezoneId?: SharedLib.DashboardInputs['timezoneId'];
  @property() apiHost?: SharedLib.DashboardInputs['apiHost'];
  @property({ type: Object }) itemDimensions: SharedLib.DashboardInputs['itemDimensions'];
  // TODO: Shouldn't be properties?
  // TODO: Accessible from component -> make private?
  // Dashboard version
  @property({ attribute: false }) _containerId: string;
  @property({ attribute: false }) _iframeId: string;
  @property({ attribute: false }) params!: SharedLib.DashboardContentParams;
  @property({ attribute: false }) _iframeUrl?: string;
  @property({ attribute: false }) iframeLoaded: boolean;
  @property({ attribute: false }) promises: SharedLib.DashboardPromises;

  // Styles
  @property({ attribute: false }) iframeStyle: SharedLib.IframeStyles = {};
  @property({ attribute: false }) _containerStyle: SharedLib.ContainerStyles = {};
  @property({ attribute: false }) _loaderStyle: SharedLib.LoaderStyles = {};
  @property({ attribute: false }) _lcStyle: SharedLib.LcStyles = {};
  @property({ attribute: false }) _loadingCircleStyle: SharedLib.PlStyles = {};
  @property({ attribute: false }) _containerLoaderStyle: SharedLib.ContainerLoaderStyles = {};
  @property({ attribute: false }) _loaderMsg = '';
  @property({ attribute: false }) containerWidth?: number;
  @property({ attribute: false }) dimensions?: SharedLib.DashboardDimensions;
  @property({ attribute: false }) currentScreenMode?: SharedLib.ScreenMode;
  @property({ attribute: false }) _itemEmbedding = false;
  @property({ attribute: false }) browserError = false;
  @property({ attribute: false }) browserErrorMsg = '';
  @property({ attribute: false }) dashboardError = false;
  @property({ attribute: false }) browserNotSupported = [];
  @property({ attribute: false }) VERSION = pkg.version + '-wc-cumulio';
  _loadTimeoutHandle?: number;
  _onResize: () => void;
  loaderSize = 32;
  borderWidth = 3;
  minHeight = 400;
  iframeHeight?: string;
  iframeWidth?: string;
  _resizeObserver: ResizeObserver;

  constructor(inputParameters?: Record<keyof CumulioDashboard, any>) {
    super();
    this._containerId = SharedLib.uuidv4();
    this._iframeId = `cio-dashboard-${this._containerId}`;
    this.iframeLoaded = false;
    this.promises = {};
    // Params
    this.setInputParameters(inputParameters);
    this.params = this._setOptions();
    this._onResize = () => {
      // Set width of container
      if (this.shadowRoot?.querySelector('.dashboard-container')) {
        // this.containerWidth = this.shadowRoot.querySelector(".dashboard-container").clientWidth;
        this.containerWidth = this.container.clientWidth;
        // Set the new screenMode if screenMode can be switched
        if (
          this.params.switchScreenModeOnResize &&
          this.params.screenMode === 'auto'
        ) {
          for (const d of SharedLib.EXTENTS) {
            if (
              d.width[0] <= window.innerWidth &&
              window.innerWidth <= d.width[1]
            ) {
              this.currentScreenMode = d.mode;
            }
          }
        }
        // Calculate new dimensions
        // Make sure the dashboard is loaded before calculating dim, else results in error
        if (this.iframeLoaded) {
          this._calculateDimAfterDashboardLoaded();
        }
      }
    }
    this._resizeObserver = new ResizeObserver((entries) => {
      // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }

        this._onResize();
      });
    });
  }

  private setInputParameters(inputParameters?: Record<keyof CumulioDashboard, any>): void {
    if (!inputParameters) {
      return;
    }

    const params: (keyof SharedLib.DashboardInputs)[] = [
      'dashboardId', 'dashboardSlug', 'itemId', 'language', 'qeVersion', 'screenMode', 'switchScreenModeOnResize',
      'accentColor', 'mainColor', 'authToken', 'authKey', 'editMode', 'loaderBackground', 'loaderFontColor',
      'loaderSpinnerColor', 'loaderSpinnerBackground', 'timezoneId', 'itemDimensions', 'apiHost'
    ];

    for (const key of params) {
      const value = inputParameters[key];

      if (value) {
        // TODO: olegpetriienko | 05/4/2022 | Check if we can get rid of "any" here
        (this as any)[key] = value;
      }
    }

    if (inputParameters.appServer) {
      this.appServer = SharedLib.addTrailingSlash(inputParameters.appServer);
    }
  }

  // Set resize event listener
  connectedCallback(): void {
    super.connectedCallback();
  }

  // Set resize event listener
  disconnectedCallback() {
    super.disconnectedCallback();
    this._resizeObserver.disconnect();
    // reject existing promises
    for (const promiseKey of Object.keys(this.promises)) {
      this.promises[promiseKey].reject(
        new Error('Dashboard component was removed')
      );
    }
  }

  firstUpdated() {
    CumulioService._components.push(this);
    // Initial container width
    this.containerWidth = this.container.clientWidth;
    this.params = this._setOptions();
    // extend & test validity params + set itemEmbedding
    SharedLib._extendAndValidate(this);

    // test browser compatibility: svg, flexbox, postmessage / set browserNotSupported for unsupported browser
    SharedLib._browserCheck(this);

    // Setup loader
    this._setupLoader();
    if (!this.params.error) {
      this._loaderStyles();

      // create the iframe event listener -> this will on receive of message loaded hide the loader and show the dashboard
      _setEventHandler();
      // generate url & set iframe url
      this._iframeUrl = SharedLib.createIframeUrl(this);
      this.iframe?.setAttribute('src', this._iframeUrl);
      // Calculate iframe style
      this.iframeStyle = SharedLib.calculateIframeStyles(this);
    }
    if (this.container) {
      this._resizeObserver.observe(this.container);
    }
  }

  // Reload dashboard when triggerproperty changes
  updated(changedProperties: PropertyValues) {
    const triggerProperties = new Set([
      'dashboardId',
      'dashboardSlug',
      'itemId',
      'language',
      'qeVersion',
      'editMode',
      'screenMode',
      'switchScreenModeOnResize',
      'authToken',
      'authKey',
      'appServer',
      'timezoneId',
      'itemDimensions'
    ]);
    for (const [propertyName] of changedProperties.entries()) {
      if (triggerProperties.has(propertyName as string) && propertyName !== 'editMode') {
        this.iframeLoaded = false;
        if (this._loadTimeoutHandle) {
          clearTimeout(this._loadTimeoutHandle);
        }
        this._loadTimeoutHandle = setTimeout(() => {
          console.error(
            'Failed to load dashboard, appServer is likely down or unreachable.'
          );
        }, 15 * 1000);
        // extend & test validity params + set itemEmbedding
        this.params = this._setOptions();
        SharedLib._extendAndValidate(this);

        this._setupLoader();
        this._loaderStyles();
        // generate url & set iframe url
        this._iframeUrl = SharedLib.createIframeUrl(this);

        if (this.iframe) {
          this._updateIframeUrl();
        }

        // Calculate iframe style
        this.iframeStyle = SharedLib.calculateIframeStyles(this);
      }
      else if (triggerProperties.has(propertyName as string) && propertyName === 'editMode') {
        // Special case for editMode parameter: don't fully reload the dashboard, but call the setEditMode postMessage for a smooth transition.
        // Only when the dashboard has already loaded, or errors will occur.
        if (this.iframeLoaded) {
          this.setEditMode(this.editMode);
        }

        this.params = this._setOptions();
      }
    }
  }

  // Set params
  _setOptions() {
    const defaultParameters: SharedLib.DashboardContentParams = {
      dashboardId: this.dashboardId,
      dashboardSlug: this.dashboardSlug
    };

    if (this.itemId) {
      defaultParameters.itemId = this.itemId;
      this._itemEmbedding = true;
    }
    else {
      this._itemEmbedding = false;
    }
    if (this.language) {
      defaultParameters.language = this.language;
    }
    if (this.qeVersion) {
      defaultParameters.qeVersion = this.qeVersion;
    }
    if (this.screenMode) {
      defaultParameters.screenMode = this.screenMode;
    }
    defaultParameters.switchScreenModeOnResize = this.switchScreenModeOnResize;
    if (this.authToken) {
      defaultParameters.token = this.authToken;
    }
    if (this.authKey) {
      defaultParameters.key = this.authKey;
    }
    if (this.editMode) {
      defaultParameters.editMode = this.editMode;
    }
    if (this.mainColor) {
      defaultParameters.mainColor = this.mainColor;
    }
    if (this.accentColor) {
      defaultParameters.accentColor = this.accentColor;
    }
    if (this.loaderBackground) {
      defaultParameters.loaderBackground = this.loaderBackground;
    }
    if (this.loaderFontColor) {
      defaultParameters.loaderFontColor = this.loaderFontColor;
    }
    if (this.loaderSpinnerColor) {
      defaultParameters.loaderSpinnerColor = this.loaderSpinnerColor;
    }
    if (this.loaderSpinnerBackground) {
      defaultParameters.loaderSpinnerBackground = this.loaderSpinnerBackground;
    }
    if (this.timezoneId) {
      defaultParameters.timezoneId = this.timezoneId;
    }
    if (this.itemDimensions) {
      defaultParameters.itemDimensions = this.itemDimensions;
    }
    if (this.appServer) {
      this.appServer = SharedLib.addTrailingSlash(this.appServer);
      defaultParameters.appServer = this.appServer;
    }
    else {
      this.appServer = SharedLib.addTrailingSlash(SharedLib.DEFAULT_PARAMETERS.appServer);
      defaultParameters.appServer = this.appServer;
    }
    if (this.apiHost) {
      this.apiHost = SharedLib.addTrailingSlash(this.apiHost);
      defaultParameters.apiHost = this.apiHost;
    }
    else {
      this.apiHost = this.appServer.endsWith('cumul.io/')
        ? this.appServer.replace('app', 'api')
        : this.appServer;
      defaultParameters.apiHost = this.apiHost;
    }
    return defaultParameters;
  }

  _updateIframeUrl() {
    SharedLib.updateIframeUrl(this.iframe, this._iframeUrl);
  }

  // Setup loader / browser error
  _setupLoader() {
    const data = SharedLib.getLoader(this);
    this._loaderMsg = data.loadingMsg;

    // Browser error
    if (data.errorMsg) {
      this.browserError = true;
      this.browserErrorMsg = data.errorMsg;
      this._displayError();
    }
  }

  _displayError() {
    // Show iframe error
    if (this.dashboardError) {
      this._loaderStyles();
      this.iframeStyle.opacity = 1;
    }
    // Show browser error
    if (this.browserError) {
      this._containerStyle.display = 'none';
    }
  }

  _calculateDimAfterDashboardLoaded() {
    this.dashboardError = false;
    this._loaderStyles();
    SharedLib._setCalculatedDimensions(this);
  }

  // Set loader styles
  _loaderStyles() {
    this._containerStyle = SharedLib.containerStyles(this);
    this._loaderStyle = SharedLib.loaderStyles(this);
    this._loadingCircleStyle = SharedLib.plStyles(this);
    this._containerLoaderStyle = SharedLib.containerLoaderStyles(this);
    this._lcStyle = SharedLib.lcStyles(this);
  }

  static get styles() {
    return [dashboardStyles];
  }

  render() {
    return html`
      <div class='dashboard-container' style=${styleMap(this._containerStyle as unknown as StyleInfo)}>
        ${this.dashboardId || this.dashboardSlug
          ? html`
            <div
              class='cio-container-loader'
              id='cio-container-loader-${this._containerId}'
              style=${styleMap(this._containerLoaderStyle as unknown as StyleInfo)}
            >
              <div
                class='cio-loader'
                id='cio-loader-${this._containerId}'
                style=${styleMap(this._loaderStyle as unknown as StyleInfo)}
              >
                <div class='cio-lc' style=${styleMap(this._lcStyle as unknown as StyleInfo)}>
                  <div
                    class='cio-loading-circle'
                    style=${styleMap(this._loadingCircleStyle as unknown as StyleInfo)}
                  ></div>
                  ${this._itemEmbedding
                    ? ''
                    : html`
                      <div
                        class='cio-loading-message'
                        style=${styleMap({ color: this.loaderFontColor })}
                      >
                        ${this._loaderMsg}
                      </div>`}
                </div>
              </div>
            </div>

            <iframe
              class='cio-embed-dashboard'
              id='${this._iframeId}'
              name='${this._iframeId}'
              style=${styleMap(this.iframeStyle as unknown as StyleInfo)}
              frameborder='0'
            ></iframe>
          `
          : html`<!-- the dashboard will inititialize when you provide a dashboardSlug or a dashboardId -->`}
      </div>
      ${this.browserError
        ? html`
          <div id='cio-error-${this._containerId}'>
            <h3 class='cio-error'>${this.browserErrorMsg}</h3>
          </div>`
        : ''}
    `;
  }

  getDashboards(): Promise<CumulioDashboard[]> {
    return new Promise((resolve, reject) => resolve(CumulioService._components));
  }

  // Public methods
  getData(itemId: string): Promise<SharedLib.ItemData> {
    return SharedLib.getData(this, this.iframe, itemId);
  }

  getFilters(): Promise<SharedLib.FilterGroup[]> {
    return SharedLib.getFilters(this, this.iframe);
  }

  setFilters(itemId: string, filters: SharedLib.FilterInput): Promise<SharedLib.SetFilters> {
    return SharedLib.setFilters(this, this.iframe, itemId, filters);
  }

  setAuthorization(key: string, token: string): Promise<void> {
    return SharedLib.setAuthorization(this, this.iframe, key, token);
  }

  refreshData(itemId?: string): Promise<void> {
    return itemId
      ? SharedLib.refreshData(this, this.iframe, itemId)
      : SharedLib.refreshData(this, this.iframe);
  }

  reloadDashboard(): Promise<void> {
    return SharedLib.reloadDashboard(this, this.iframe);
  }

  exportDashboard(format?: SharedLib.ExportType): Promise<SharedLib.ExportDashboard> {
    return format
      ? SharedLib.exportDashboard(this, this.iframe, format)
      : SharedLib.exportDashboard(this, this.iframe);
  }

  getAccessibleDashboards(): Promise<SharedLib.AccessibleDashboard[]> {
    return SharedLib.getAccessibleDashboards(this, this.iframe);
  }

  setEditMode(editMode?: SharedLib.DashboardEditMode): Promise<SharedLib.SetEditMode> {
    return SharedLib.setEditMode(this, this.iframe, editMode);
  }

  setPreview(preview: boolean): Promise<SharedLib.SetPreview> {
    return SharedLib.setPreview(this, this.iframe, preview);
  }
}
